import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';

function toDate(value: string | number | Date): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  return parseISO(value);
}

/**
 * Short timezone label for a given instant in the viewer's local zone, e.g.
 * "GMT+5:30", "AEST", "PST". Computed per-instant so it stays DST-correct. Empty
 * string if the platform can't resolve it (then callers omit the suffix).
 */
export function tzLabel(value: string | number | Date = Date.now()): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(
      toDate(value),
    );
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Absolute timestamp in the viewer's local timezone, ALWAYS labelled with the
 * zone (e.g. "2026-07-06 14:30:00 GMT+5:30") so a reader is never left guessing
 * which zone a time is in. Every admin date-time display floors through here.
 */
export function formatDateTime(value: string | number | Date): string {
  try {
    const d = toDate(value);
    const tz = tzLabel(d);
    return `${format(d, 'yyyy-MM-dd HH:mm:ss')}${tz ? ` ${tz}` : ''}`;
  } catch {
    return String(value);
  }
}

export function formatDate(value: string | number | Date): string {
  try {
    return format(toDate(value), 'yyyy-MM-dd');
  } catch {
    return String(value);
  }
}

/**
 * Display a date-only value — a logical calendar day stored as midnight GMT
 * (e.g. tracker `dueDate`/`startDate`) — as that GMT calendar date. Timezone
 * independent by design: `formatDate` renders the viewer's LOCAL day, which
 * shifts a GMT-midnight value to the previous day for anyone west of GMT.
 *
 * FIX(admin-date-only-format): Before: date-only fields went through
 * `formatDate`, off by one day west of GMT. After: callers of date-only fields
 * use this. Accepts the same inputs as `formatDate` (ISO string, epoch ms,
 * Date); a bare `YYYY-MM-DD` string is returned as-is.
 */
export function formatDateOnly(value: string | number | Date): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  try {
    const d = toDate(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toISOString().slice(0, 10);
  } catch {
    return String(value);
  }
}

const BUILT_IN_ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  org_admin: 'Org Admin',
  project_manager: 'Project Manager',
  team_lead: 'Team Lead',
  moderator: 'Moderator',
  member: 'Member',
};

/**
 * Human label for a raw server role slug (`superadmin`, `admin`, a custom
 * `release_managers` slug …). Built-ins get their canonical names; anything
 * else is title-cased from the slug.
 */
export function formatRoleLabel(slug: string): string {
  const known = BUILT_IN_ROLE_LABELS[slug];
  if (known) return known;
  const words = slug.split(/[_\-\s]+/).filter(Boolean);
  if (words.length === 0) return slug;
  return words.map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
}

export function formatRelative(value: string | number | Date): string {
  try {
    return formatDistanceToNowStrict(toDate(value), { addSuffix: true });
  } catch {
    return String(value);
  }
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat().format(n);
}

export function formatCurrency(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function formatPercent(value: number, fractionDigits = 1): string {
  return `${(value * 100).toFixed(fractionDigits)}%`;
}

/**
 * The emoji of a docs space's `icon`, or '' when it has none worth printing:
 * blank, the legacy '\u{1F4C1}' default, or an `avatar:<id>` gallery illustration
 * (the admin has no artwork, and must never show the raw prefix).
 */
export function spaceIconEmoji(icon: string | null | undefined): string {
  const v = (icon ?? '').trim();
  return v === '' || v === '\u{1F4C1}' || v.startsWith('avatar:') ? '' : v;
}
